/**
 * CaptureHome (SK-HOME-2, T-21 / §7.M3) — the record circle, the sole capture
 * entry point. TAP ONLY: no wake-phrase code path, no "Hey Mark" subtitle, no
 * always-listening logic anywhere in this file (TW-1g-WAKEWORD grep guard).
 * The perf mark at tap is the start of the tap→recording-live budget (§8.4,
 * T-27).
 *
 * Design reconciliation (Minimal Dark Mode "1 Record"): the control renders as
 * an amber circle bearing a dark-ink mic glyph and NO text label — the round
 * amber shape and any ring are the skin's job (`data-skin-slot="SK-HOME-2"`),
 * this file emits the glyph + the accessible label. A LONG press opens the
 * ModalityPicker (Upload / Video / Text alt-ingress, PWA-SCREEN-SKILL-MAP §1),
 * which renders NESTED here and dims the record circle to 35% while open
 * (the preview's `recordOpacity`); a short tap/release before the threshold
 * starts recording as usual.
 */

import { useEffect, useRef, useState } from "preact/hooks";
import { mintCaptureId } from "../outbox/idempotency";
import { markOpened } from "../perf/measure";
import {
  classifyMicrophoneFailure,
  microphoneRecoveryCopy,
  queryMicrophonePermission,
  requestMicrophoneStream,
  type MicrophonePermissionState,
} from "../media/microphone-access";
import { ModalityPicker, type ModalityOption } from "./ModalityPicker";
import { Chip } from "./Chip";

const LONG_PRESS_MS = 500;

export interface CaptureHomeProps {
  onStartCapture: (capture_id: string, stream: MediaStream) => void;
  /** Long-press → alt-ingress. When absent, the long-press affordance is
   * inert (tap-only). The picker itself is owned here (nested overlay); the
   * host is handed only the resolved selection to drive the flow. */
  onSelectModality?: (modality: ModalityOption) => void;
  queryMicrophone?: () => Promise<MicrophonePermissionState>;
  requestMicrophone?: () => Promise<MediaStream>;
}

export function CaptureHome({
  onStartCapture,
  onSelectModality,
  queryMicrophone = queryMicrophonePermission,
  requestMicrophone = requestMicrophoneStream,
}: CaptureHomeProps) {
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFired = useRef(false);
  const [modalityOpen, setModalityOpen] = useState(false);
  const [microphoneState, setMicrophoneState] = useState<MicrophonePermissionState>("checking");
  const requestingMicrophone = useRef(false);

  useEffect(() => {
    let mounted = true;
    const check = () => {
      if (document.visibilityState !== "visible") return;
      void queryMicrophone().then((state) => {
        if (mounted) setMicrophoneState(state);
      });
    };
    check();
    document.addEventListener("visibilitychange", check);
    return () => {
      mounted = false;
      document.removeEventListener("visibilitychange", check);
    };
  }, [queryMicrophone]);

  async function startCapture() {
    if (requestingMicrophone.current) return;
    requestingMicrophone.current = true;
    setMicrophoneState("checking");
    try {
      const stream = await requestMicrophone();
      setMicrophoneState("granted");
      const capture_id = mintCaptureId();
      markOpened(capture_id);
      // Hand the stream acquired under the tap gesture directly to the active
      // recorder. Re-acquiring after navigation is unreliable on iOS Safari.
      onStartCapture(capture_id, stream);
    } catch (error) {
      setMicrophoneState(classifyMicrophoneFailure(error));
    } finally {
      requestingMicrophone.current = false;
    }
  }

  function handlePointerDown() {
    longPressFired.current = false;
    if (!onSelectModality) return;
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      setModalityOpen(true);
    }, LONG_PRESS_MS);
  }

  function clearLongPressTimer() {
    if (longPressTimer.current !== null) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }

  function handlePointerUp() {
    clearLongPressTimer();
    if (!longPressFired.current) void startCapture();
  }

  function handlePointerLeave() {
    clearLongPressTimer();
  }

  const microphoneBlocked = microphoneState === "denied" || microphoneState === "unavailable";

  return (
    <section
      data-skin-slot="SK-HOME-2"
      data-testid="capture-home"
      style={{ position: "relative", ...(microphoneBlocked ? { alignItems: "center" } : {}) }}
    >
      {!microphoneBlocked && (
        <>
          <div class="capture-orientation" data-testid="capture-orientation">
            <strong>Capture a field update</strong>
            <p>
              Record what happened, review the transcript, then choose Daily Log,
              Action, Decision, or a report.
            </p>
            <span>Tap for voice · hold for Text or Upload</span>
          </div>
          <button
            type="button"
            data-testid="tap-to-record"
            aria-label="Tap to record"
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerLeave}
            style={{ opacity: modalityOpen ? 0.35 : 1, transition: "opacity 0.2s" }}
          >
            <svg
              width="56"
              height="56"
              viewBox="0 0 24 24"
              fill="none"
              stroke-width="1.6"
              stroke-linecap="round"
              style={{ display: "block", margin: "0 auto", stroke: "var(--mark-ink-on-amber)" }}
              aria-hidden="true"
            >
              <rect x="9" y="3" width="6" height="11" rx="3" stroke="none" style={{ fill: "var(--mark-ink-on-amber)" }} />
              <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0" />
              <line x1="12" y1="18" x2="12" y2="21" />
            </svg>
          </button>
        </>
      )}

      {microphoneBlocked && (
        <aside
          data-testid="microphone-permission-recovery"
          role="alert"
          style={{
            width: "min(420px, calc(100vw - 48px))",
            margin: "0 auto",
            padding: "24px",
            border: "1px solid var(--mark-bg-surface-alt)",
            borderRadius: "20px",
            background: "var(--mark-bg-surface)",
            color: "var(--mark-text-primary)",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            gap: "14px",
          }}
        >
          <strong>Microphone access required for voice recording</strong>
          <p style={{ margin: 0, color: "var(--mark-text-muted)", lineHeight: 1.45 }}>{microphoneRecoveryCopy()}</p>
          <Chip
            data-testid="microphone-try-again"
            onClick={() => void startCapture()}
          >
            Try microphone again
          </Chip>
          {onSelectModality && (
            <Chip
              data-testid="microphone-use-other"
              onClick={() => setModalityOpen(true)}
            >
              Use Text or Upload
            </Chip>
          )}
        </aside>
      )}

      {onSelectModality && (
        <ModalityPicker
          open={modalityOpen}
          onSelect={(m) => {
            setModalityOpen(false);
            onSelectModality(m);
          }}
          onClose={() => setModalityOpen(false)}
        />
      )}
    </section>
  );
}
